/**
 * Visits panel Step C (labels-v2 PROPOSAL-C.md, founder-approved
 * 2026-10-13) — the shared rules behind "one row per bot name" and the
 * purpose filters (All · AI agents · Search · SEO & tools · Suspicious ·
 * Orders · People).
 *
 * Two consumers must agree byte for byte:
 *
 *   - the API (`services/api/.../visit-segments-sql.ts`) mirrors
 *     `uaGroupToken` in SQL to build the group key, and reads the purpose
 *     tables below as bound `text[]` parameters for the filters;
 *   - the dashboard qualifies a token row's name with `uaGroupToken`
 *     ("Unknown bot · python-requests") and labels its purpose with
 *     `visitCategoryForToken` (non-registry tools only).
 *
 * A real-PG test runs the SQL mirror and this function over the same UA
 * fixture list (`services/api/test/v1/visit-segments-sql-real-pg.test.ts`).
 *
 * Pure. No I/O.
 */
import { BUNDLED_REGISTRY, SILL_INTERNAL_TOOLS } from '@sill/types';
/**
 * Browser-engine and platform tokens that are never a bot's own name.
 * A UA made only of these (and with no `compatible; Name…` slot) has no
 * bot token: it keeps its exact-UA group, so generic browser-looking UAs
 * never merge into one giant row. `GENERIC_UA_TOKENS` in `labels.ts` plus
 * a few more browser / OS build tokens.
 */
export const VISIT_GENERIC_UA_TOKENS = [
    'mozilla', 'applewebkit', 'khtml', 'gecko', 'chrome', 'safari', 'version', 'mobile', 'firefox', 'edge', 'edg',
    'opr', 'trident', 'windows', 'linux', 'x11', 'crios', 'fxios', 'like', 'chromium', 'wv', 'samsungbrowser',
    'ucbrowser', 'yabrowser', 'opera', 'presto', 'build', 'gsa', 'vivaldi', 'miuibrowser', 'huaweibrowser',
    'qqbrowser',
];
/**
 * The rule is written as plain string steps (not one regex with captures
 * and lookaheads) so the Postgres mirror stays cheap per row: captures
 * and lookaheads made the first version ~15 s per 150k rows. Every regex
 * here is anchored and capture-free, and runs the same in JS and PG.
 *
 *   1. compatible slot: the FIRST "compatible;" (any case), at the start or
 *      after a character outside [a-z0-9_], then spaces, then a name
 *      `[a-z][a-z0-9._-]{1,40}` followed by `;`, `)` or `/` + a character
 *      other than space, `;`, `)`. Never "mozilla".
 *   2. else the LEADING `name/version` token (`[a-z][a-z0-9._-]{0,32}/`
 *      then a version character), unless it is a browser-engine token.
 *
 * Lower-casing is ASCII-only (Postgres `lower()` maps one character to
 * one character, so offsets agree; token characters are ASCII).
 */
export const COMPAT_MARKER = 'compatible;';
/** Name + terminator after the compatible slot (anchored, no captures). */
export const COMPAT_VALID_RE_SOURCE = '^[a-z][a-z0-9._-]{1,40}(?:[;)]|/[^ ;)])';
export const COMPAT_NAME_RE_SOURCE = '^[a-z][a-z0-9._-]{1,40}';
/** A leading `name/version` token (anchored, no captures). */
export const LEAD_TOKEN_RE_SOURCE = '^[a-z][a-z0-9._-]{0,32}/[a-z0-9._-]';
const COMPAT_VALID_RE = new RegExp(COMPAT_VALID_RE_SOURCE);
const COMPAT_NAME_RE = new RegExp(COMPAT_NAME_RE_SOURCE);
const LEAD_TOKEN_RE = new RegExp(LEAD_TOKEN_RE_SOURCE);
const GENERIC = new Set(VISIT_GENERIC_UA_TOKENS);
/** Postgres `trim()` (spaces only). */
function trimSpaces(s) {
    return s.replace(/^ +| +$/g, '');
}
function asciiLower(s) {
    return s.replace(/[A-Z]/g, (c) => c.toLowerCase());
}
/** [start, end) of the token in the trimmed UA, or null. */
function tokenSpan(ua) {
    const low = asciiLower(ua);
    const i = low.indexOf(COMPAT_MARKER);
    if (i >= 0 && (i === 0 || !/[a-z0-9_]/.test(low[i - 1]))) {
        const after = low.slice(i + COMPAT_MARKER.length);
        const rest = after.replace(/^ +/, '');
        if (COMPAT_VALID_RE.test(rest)) {
            const name = COMPAT_NAME_RE.exec(rest)[0];
            if (name !== 'mozilla') {
                const start = i + COMPAT_MARKER.length + (after.length - rest.length);
                return [start, start + name.length];
            }
        }
    }
    if (LEAD_TOKEN_RE.test(low)) {
        const lead = low.slice(0, low.indexOf('/'));
        if (!GENERIC.has(lead))
            return [0, lead.length];
    }
    return null;
}
/**
 * The bot's product token, in the case the UA uses it, or `null` when the
 * UA carries none:
 *
 *   "python-requests/2.31.0"                            → "python-requests"
 *   "Mozilla/5.0 (compatible; FooBot/1.2; +https://x)"  → "FooBot"
 *   "Mozilla/5.0 (Macintosh; …) Chrome/131 Safari/537"  → null
 */
export function uaGroupToken(user_agent) {
    if (typeof user_agent !== 'string')
        return null;
    const ua = trimSpaces(user_agent);
    if (ua.length === 0)
        return null;
    const span = tokenSpan(ua);
    return span === null ? null : ua.slice(span[0], span[1]);
}
/** The group-key form of {@link uaGroupToken}: lower-cased. */
export function uaGroupTokenKey(user_agent) {
    return uaGroupToken(user_agent)?.toLowerCase() ?? null;
}
export const VISIT_SEGMENTS = ['bots', 'ai', 'search', 'seo', 'suspicious', 'orders', 'people'];
export const PURPOSE_SEGMENTS = ['ai', 'search', 'seo'];
/**
 * Registry category → purpose chip. "SEO & tools" holds SEO crawlers,
 * uptime monitors, link previews and Sill's own tools. `hostile` and
 * unknown bots sit in no purpose chip (they show under All).
 */
export function purposeSegmentForCategory(category) {
    switch (category) {
        case 'ai_crawler':
        case 'ai_assistant':
            return 'ai';
        case 'search':
            return 'search';
        case 'seo':
        case 'monitoring':
        case 'social_preview':
        case 'internal':
            return 'seo';
        default:
            return null;
    }
}
/**
 * Well-known bots that are not in the bundled registry, by product token
 * (lower case). Mirrors the names `inferAgentFromUserAgent` knows in
 * `labels.ts`, so "AhrefsBot" reads (and filters) as an SEO crawler.
 */
export const UNREGISTERED_TOKEN_CATEGORY = {
    ahrefsbot: 'seo',
    semrushbot: 'seo',
    mj12bot: 'seo',
    dotbot: 'seo',
    dataforseobot: 'seo',
    siteauditbot: 'seo',
    'magpie-crawler': 'seo',
    blexbot: 'seo',
    serpstatbot: 'seo',
    barkrowler: 'seo',
    awariobot: 'seo',
    mentionbot: 'seo',
    uptimerobot: 'monitoring',
    'github-camo': 'monitoring',
    ia_archiver: 'search',
    seznambot: 'search',
};
const CATEGORY_BY_AGENT_ID = (() => {
    const m = new Map();
    for (const seed of BUNDLED_REGISTRY) {
        if (seed.category !== undefined)
            m.set(seed.agent_id, seed.category);
    }
    for (const t of SILL_INTERNAL_TOOLS)
        m.set(t.agent_id, 'internal');
    return m;
})();
/**
 * Product token (lower case) → category, for visits the server did not
 * match to a registry id. ONLY the non-registry tools above (SEO crawlers,
 * monitors): a token that copies a registry brand ("GPTBot", "Googlebot",
 * a Sill tool) gets NO purpose unless the server matched the agent id
 * (security review L1: an unmatched claim must not read, or filter, as the
 * brand's purpose).
 */
const CATEGORY_BY_TOKEN = new Map(Object.entries(UNREGISTERED_TOKEN_CATEGORY));
/** Registry category for a matched agent id (`null` when unknown). */
export function visitCategoryForAgentId(agent_id) {
    if (typeof agent_id !== 'string')
        return null;
    return CATEGORY_BY_AGENT_ID.get(agent_id) ?? null;
}
/** Category for a lower-cased product token (`null` when unknown). */
export function visitCategoryForToken(token_key) {
    if (typeof token_key !== 'string')
        return null;
    return CATEGORY_BY_TOKEN.get(token_key.toLowerCase()) ?? null;
}
/** Registry agent ids whose category falls in a purpose chip. Sorted. */
export function purposeAgentIds(segment) {
    return [...CATEGORY_BY_AGENT_ID.entries()]
        .filter(([, cat]) => purposeSegmentForCategory(cat) === segment)
        .map(([id]) => id)
        .sort();
}
/** Lower-cased product tokens whose category falls in a purpose chip. Sorted. */
export function purposeTokens(segment) {
    return [...CATEGORY_BY_TOKEN.entries()]
        .filter(([, cat]) => purposeSegmentForCategory(cat) === segment)
        .map(([tok]) => tok)
        .sort();
}
// ---------------------------------------------------------------------------
// Meaningful probing (founder feedback 2026-10-14: alerts must be rare)
// ---------------------------------------------------------------------------
/** A row shows the Probing alert only with at least this many probe requests… */
export const PROBE_ALERT_MIN_HITS = 5;
/** …and when they are at least this share of the row's visits (requests). */
export const PROBE_ALERT_MIN_SHARE = 0.25;
/**
 * Group keys of senders with NO registry name: unregistered bot tokens
 * (`a.tok.`), browser-looking UAs with no bot token (`a.ua.`), empty UAs
 * (`a.noua`), and ADR-0253 unverified A2A callers (`a.a2a`). Registry
 * rows (`a.id.`), Mismatch, people and one-record rows never match. The
 * SQL mirror lives in `visit-segments-sql.ts`.
 */
export const UNNAMED_SENDER_GROUP_KEY_RE_SOURCE = '^a\\.(tok\\.|ua\\.|noua$|a2a$)';
/**
 * Registry-named rows (`a.id.<agent>`, not a Sill tool): the pill shows
 * when their NON-Confirmed visits made at least this many requests to
 * UNIVERSAL probe classes (secrets, keys, traversal, /proc, ::$DATA,
 * CGI, webshells; never `.php` / wp-login / xmlrpc). No share threshold:
 * the real crawler never requests `/.env`, so a few such requests mean
 * someone is borrowing the name (founder feedback 2026-10-14, vbout.com).
 */
export const PROBE_ALERT_NAMED_MIN_UNIVERSAL_HITS = 3;
/** Group keys of registry-named rows (Sill tools excluded separately). */
export const NAMED_SENDER_GROUP_KEY_RE_SOURCE = '^a\\.id\\.';
/**
 * ADR-0246 — the "Unknown scanner" row: every disguised probe of a site
 * (a browser-looking UA requesting `/.env`, `/.git/config`, a webshell…;
 * `identification_match.disguised_probe`). Never people. Every record in it
 * is a probe by construction, so it always shows the Probing pill and is
 * always in Suspicious.
 */
export const SCANNER_GROUP_KEY = 'a.scan';
/**
 * The one rule for the Probing pill and the Suspicious chip (besides
 * Mismatch). The SQL mirror is `probeAlertSql`.
 *
 *   - A sender with no registry name: probe requests at least
 *     `PROBE_ALERT_MIN_HITS` and at least `PROBE_ALERT_MIN_SHARE` of its
 *     visits. `visits` and `probe_hits` share a unit: one embed beacon is
 *     one visit (folded into `visit_count`) and one `agent_page_hit` row.
 *   - Sill's own tool rows (`isSillToolGroupKey`): the same thresholds,
 *     over universal probe classes from unverified visits only (the
 *     server counts `probe_hits` that way).
 *   - A registry-named row: `probe_hits_universal` (universal classes,
 *     non-Confirmed visits) at least `PROBE_ALERT_NAMED_MIN_UNIVERSAL_HITS`.
 *     Someone is using the name; the real agent never asks for secrets.
 *   - Mismatch, people and one-record rows: never (Mismatch has its own
 *     treatment).
 *   - The Unknown scanner row (`SCANNER_GROUP_KEY`, ADR-0246): always.
 */
export function isMeaningfulProbing(g) {
    const hits = g.probe_hits ?? 0;
    // ADR-0246 — the Unknown scanner row is all probes, always.
    if (g.group_key === SCANNER_GROUP_KEY)
        return true;
    // Sill tool rows count only UNIVERSAL probe classes from visits Sill did
    // not verify (server side), so an unsigned "Sill-…" UA probing /.env
    // still alerts (security review m1). Verified Sill visits never count.
    if (new RegExp(UNNAMED_SENDER_GROUP_KEY_RE_SOURCE).test(g.group_key) || isSillToolGroupKey(g.group_key)) {
        return hits >= PROBE_ALERT_MIN_HITS && hits >= g.visits * PROBE_ALERT_MIN_SHARE;
    }
    if (new RegExp(NAMED_SENDER_GROUP_KEY_RE_SOURCE).test(g.group_key)) {
        return (g.probe_hits_universal ?? 0) >= PROBE_ALERT_NAMED_MIN_UNIVERSAL_HITS;
    }
    return false;
}
/** Is this a registry-named row the impostor rule applies to (not a Sill tool)? */
export function isNamedSenderGroupKey(group_key) {
    return new RegExp(NAMED_SENDER_GROUP_KEY_RE_SOURCE).test(group_key) && !isSillToolGroupKey(group_key);
}
// ---------------------------------------------------------------------------
// Sill's own tools: one row per tool name (founder feedback 2026-10-14)
// ---------------------------------------------------------------------------
/**
 * Sill tool agent id → the canonical id its visits group under: every
 * tool sharing a `name` (the legacy `Sill-Analyzer/` scanner UA and the
 * current `Sill-Readiness/` one are both "Site scanner") maps to the LAST
 * listed tool with that name (the current one). Mirrored in SQL by
 * `visitGroupsPipelineSql`.
 */
export const SILL_TOOL_CANONICAL_ID = (() => {
    const byName = new Map();
    for (const t of SILL_INTERNAL_TOOLS)
        byName.set(t.name, t.agent_id);
    return new Map(SILL_INTERNAL_TOOLS.map((t) => [t.agent_id, byName.get(t.name)]));
})();
/** Lower-cased Sill tool product token → canonical tool id. */
export const SILL_TOOL_ID_BY_TOKEN = new Map(SILL_INTERNAL_TOOLS.map((t) => [t.token.toLowerCase(), SILL_TOOL_CANONICAL_ID.get(t.agent_id)]));
/**
 * The group-key agent part (`id.<canonical>`) for a Sill tool visit, by
 * matched registry id, else by the UA's product token; `null` otherwise.
 */
export function sillToolGroupAgent(agent_id, user_agent) {
    if (typeof agent_id === 'string') {
        const c = SILL_TOOL_CANONICAL_ID.get(agent_id);
        if (c !== undefined)
            return `id.${c}`;
        return null;
    }
    const tok = uaGroupTokenKey(user_agent);
    const c = tok !== null ? SILL_TOOL_ID_BY_TOKEN.get(tok) : undefined;
    return c !== undefined ? `id.${c}` : null;
}
/**
 * Every group key a Sill tool visit can land under: the canonical (and
 * every) Sill tool id, as an agent or Mismatch row, and every Sill token
 * row. ONE exact list (security review m2), shared by the group-level SQL
 * (`SILL_TOOL_GROUP_KEYS`) and `isMeaningfulProbing`.
 */
export const SILL_TOOL_GROUP_KEYS = [
    ...SILL_INTERNAL_TOOLS.map((t) => `a.id.${t.agent_id}`),
    ...SILL_INTERNAL_TOOLS.map((t) => `a.tok.${t.token.toLowerCase()}`),
];
const SILL_TOOL_GROUP_KEY_SET = new Set(SILL_TOOL_GROUP_KEYS);
/** Is this a Sill tool row (`SILL_TOOL_GROUP_KEYS`)? */
export function isSillToolGroupKey(group_key) {
    return SILL_TOOL_GROUP_KEY_SET.has(group_key);
}
//# sourceMappingURL=visit-segments.js.map